// Applies a new service worker automatically, but only when reloading cannot
// destroy something the user is in the middle of.
//
// Background: assets are content-hashed and served `immutable`, index.html and
// sw.js are `no-cache`, and every deploy invalidates CloudFront, so S3 and the CDN
// already serve the new build immediately. The service worker is the layer that
// holds the old one, which is why CTRL+SHIFT+R (which bypasses it) was the only
// thing that worked. Registration uses `registerType: 'prompt'`, so a new worker
// installs and then WAITS for the app to tell it to take over.
//
// The old flow only ever showed a dismissible snackbar, so a user who missed or
// dismissed it stayed on the old build indefinitely. This keeps the snackbar as an
// opt-in, but also takes over on its own the moment it is safe.
//
// WHY A DOM/GLOBAL CHECK RATHER THAN PER-VIEW "AM I DIRTY?": 28 views and
// components carry their own dialog state and none of them track dirtiness, so
// threading a signal through each would be a large change with 28 chances to miss
// one, and a missed one reloads over real work. The checks below are global,
// cheap, and fail CLOSED: anything unrecognised counts as unsafe.

// DISCOVERY vs APPLYING. Everything below the divider decides WHEN it is safe to
// swap in a worker that is already waiting. This first section is the other half:
// getting the browser to notice a new build exists at all.
//
// The periodic check in registerServiceWorker.js is a backstop, not the main
// route, because browsers throttle timers hard in background tabs (Chrome to
// roughly once a minute, frozen entirely once a tab is discarded). Least
// reliable for exactly the user most likely to be on a stale build: the one whose
// tab sat open all afternoon. So the app also checks when it has a reason to: the
// tab becoming visible, and route changes.

// The live registration, handed over by registerServiceWorker.js. Null in dev and
// anywhere the worker never registered, which makes checkForUpdate a no-op there
// rather than something callers have to guard.
let swRegistration = null

let lastCheckAt = 0

// Route changes can come in bursts (a redirect chain, a user clicking through
// tabs), and each check is a network request. One every 30s is plenty given the
// 5-minute backstop; `force` exists so the backstop itself is never throttled.
const CHECK_THROTTLE_MS = 30000

export function setRegistration(registration) {
  swRegistration = registration
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisible)
  }
}

function onVisible() {
  if (document.visibilityState === 'visible') checkForUpdate()
}

// Ask the browser to re-fetch sw.js and compare it byte-for-byte. Served
// `no-cache`, so the common answer is a 304. Returns whether a check was actually
// issued, which is what the tests assert on.
export function checkForUpdate({ force = false } = {}) {
  if (!swRegistration || typeof swRegistration.update !== 'function')
    return false
  const now = Date.now()
  if (!force && now - lastCheckAt < CHECK_THROTTLE_MS) return false
  lastCheckAt = now
  // Rejects when offline, or when the sw.js fetch fails. Discovery is
  // best-effort: the backstop interval will come round again, and an unhandled
  // rejection here would be noise in every offline user's console.
  Promise.resolve(swRegistration.update()).catch(() => {})
  return true
}

// ---------------------------------------------------------------------------

// The registration whose `waiting` worker is ready to take over. Module-level
// rather than component state because the update can land BEFORE the root
// component mounts. App.vue used to attach its listener in created() with
// { once: true }, so an update that resolved first fired into nothing and no
// prompt ever appeared.
let pendingRegistration = null

// Set by the app so the snackbar can be shown when an update has to wait.
let notify = null

let watchTimer = null

// How often to re-check whether it has become safe. Short, because the window
// closes as soon as the user closes a dialog or switches tabs, and a missed window
// means waiting for the next service-worker update check.
const SAFETY_POLL_MS = 5000

// Counts GraphQL mutations currently in flight. Incremented/decremented by the
// Apollo link in graphql/http-link.js. Reloading mid-write is the one case that
// can lose data the user believes is saved.
let inFlightMutations = 0

export function mutationStarted() {
  inFlightMutations += 1
}

export function mutationSettled() {
  inFlightMutations = Math.max(0, inFlightMutations - 1)
}

// Exported for testing.
export function inFlightMutationCount() {
  return inFlightMutations
}

// Coarse "don't reload now" hold for multi-step operations that are not one
// GraphQL mutation: an offline Sync replaying a whole queue (its S3 PUTs are
// plain fetch, invisible to the Apollo link, and the counter dips to zero
// between items), or a location download. Shares the in-flight counter, so
// isSafeToReload() stays false for the operation's full span.
export const holdReload = mutationStarted
export const releaseReload = mutationSettled

// Whether the user is typing. Covers text inputs, textareas, selects and
// rich-text/contenteditable regions.
function isEditingField() {
  const el = typeof document !== 'undefined' ? document.activeElement : null
  if (!el) return false
  if (el.isContentEditable) return true
  const tag = (el.tagName || '').toUpperCase()
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

// Whether any Vuetify overlay is open, dialog, menu, select, date picker,
// autocomplete. One selector covers all 28 dialog-bearing components without
// touching any of them. The class is Vuetify's own (VOverlay.js applies
// `v-overlay--active` while `isActive`), not one invented here.
//
// Snackbars are excluded: VSnackbar is overlay-based too, so the persistent
// "New Version Available" snackbar was matching this check and BLOCKING the
// very update it announced, on a visible idle tab the auto-apply could never
// fire until the user dismissed it. A snackbar is a notification, not work in
// progress; reloading through one destroys nothing.
function hasOpenOverlay() {
  if (typeof document === 'undefined') return false
  return document.querySelector('.v-overlay--active:not(.v-snackbar)') !== null
}

// The decision. Reload is safe when nothing is in progress that a reload would
// throw away.
//
// A HIDDEN TAB IS THE SAFEST MOMENT, not a reason to wait: the user is not looking,
// so swapping the build costs them nothing and they return to the new one. It is
// checked first because a background tab can legitimately hold a stale focused
// input or open menu that no longer matters.
export function isSafeToReload() {
  if (typeof document === 'undefined') return false
  // An in-flight write outranks the hidden-tab shortcut: unlike a stale
  // focused input or an abandoned menu, a request on the wire is destroyed by
  // a reload whether or not anyone is looking. And a held span (an offline
  // Sync draining its queue, a location download) rides this same counter.
  if (inFlightMutations > 0) return false
  if (document.hidden) return true
  if (hasOpenOverlay()) return false
  if (isEditingField()) return false
  return true
}

// Tell the waiting worker to take over. sw.js answers `skipWaiting` by activating,
// which fires `controllerchange`, and app.vue reloads on that. So the reload is
// driven by the worker actually taking control rather than by a guessed delay.
function activate(registration) {
  const waiting = registration && registration.waiting
  if (!waiting) return false
  waiting.postMessage('skipWaiting')
  return true
}

function stopWatching() {
  if (watchTimer) {
    clearInterval(watchTimer)
    watchTimer = null
  }
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', attempt)
  }
}

// Apply now if safe; otherwise keep watching until it is. Also re-checked on
// visibilitychange, so switching away from the tab applies the update immediately
// rather than waiting out the poll.
function attempt() {
  if (!pendingRegistration) return
  if (!isSafeToReload()) return
  if (activate(pendingRegistration)) {
    pendingRegistration = null
    stopWatching()
  }
}

// Called when a new worker is waiting. Exported so registerServiceWorker.js can
// hand off, and so tests can drive it directly.
export function onUpdateReady(registration) {
  pendingRegistration = registration

  // Offer the manual route straight away. A user who wants the new build now
  // should not have to wait for a dialog to close.
  if (notify) notify(registration)

  attempt()

  if (pendingRegistration && !watchTimer) {
    watchTimer = setInterval(attempt, SAFETY_POLL_MS)
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', attempt)
    }
  }
}

// app.vue registers its snackbar here. Any update that landed BEFORE the app
// mounted is delivered immediately, which is the race the old { once: true }
// listener lost.
export function onNotify(callback) {
  notify = callback
  if (pendingRegistration) callback(pendingRegistration)
}

// The snackbar's Refresh button: apply regardless of safety, because the user
// asked for it explicitly.
export function applyUpdateNow() {
  if (!pendingRegistration) return false
  const applied = activate(pendingRegistration)
  if (applied) {
    pendingRegistration = null
    stopWatching()
  }
  return applied
}

// Test seam.
export function resetForTests() {
  pendingRegistration = null
  notify = null
  inFlightMutations = 0
  stopWatching()
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', onVisible)
  }
  swRegistration = null
  lastCheckAt = 0
}
